// 관리자 대시보드 — 반응형 최적화 (모바일/PC 대응)
// src/pages/admin/page.tsx

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import type { Profile } from '@/hooks/useAuth';
import Navbar from '@/pages/home/components/Navbar';
import StatsCharts from './components/StatsCharts';
import DrawManager from './components/DrawManager';
import ErrorLogManager from './components/ErrorLogManager';
import TermsManager from './components/TermsManager';
import ReAuthModal from './components/ReAuthModal';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import Pagination from '@/components/base/Pagination';

interface ActivityLog {
  id: number;
  user_id: string;
  action: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}

interface AgreementRecord {
  id: number;
  user_id: string;
  terms_id: number;
  agreed: boolean;
  agreed_at: string;
  ip_address: string | null;
}

interface SavedNumber {
  id: string;
  user_id: string;
  numbers: number[];
  bonus: number | null;
  generator_type: string;
  label: string | null;
  created_at: string;
}

type AdminTab = 'members' | 'activity' | 'agreements' | 'numbers' | 'stats' | 'draws' | 'errors' | 'terms';

const actionLabels: Record<string, string> = {
  login: '로그인',
  logout: '로그아웃',
  register: '회원가입',
  generate_random: '랜덤 번호 생성',
  generate_lucky: '행운 번호 생성',
  generate_smart: '스마트 추천',
  save_number: '번호 저장',
  delete_number: '번호 삭제',
};

const MEMBERS_PAGE_SIZE = 20;
const ACTIVITY_PAGE_SIZE = 20;
const AGREEMENTS_PAGE_SIZE = 20;
const NUMBERS_PAGE_SIZE = 20;

export default function AdminPage() {
  const { isAdmin, user } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('members');

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [agreements, setAgreements] = useState<AgreementRecord[]>([]);
  const [allNumbers, setAllNumbers] = useState<SavedNumber[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [membersPage, setMembersPage] = useState(1);
  const [membersTotal, setMembersTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [agreementsPage, setAgreementsPage] = useState(1);
  const [agreementsTotal, setAgreementsTotal] = useState(0);
  const [numbersPage, setNumbersPage] = useState(1);
  const [numbersTotal, setNumbersTotal] = useState(0);
  const [todayActivityCount, setTodayActivityCount] = useState(0);

  // 검색/필터 상태
  const [membersSearch, setMembersSearch] = useState('');
  const debouncedMembersSearch = useDebounce(membersSearch, 300);
  const [activityActionFilter, setActivityActionFilter] = useState('');
  const [numbersGeneratorFilter, setNumbersGeneratorFilter] = useState('');
  const [numberFilter, setNumberFilter] = useState('');

  const [reAuthExpiry, setReAuthExpiry] = useState<number | null>(null);
  const [reAuthModalOpen, setReAuthModalOpen] = useState(false);

  // 관리자 mutation 처리 상태(역할 변경/번호 삭제)
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [deletingNumberId, setDeletingNumberId] = useState<string | null>(null);
  const [deleteNumberConfirmId, setDeleteNumberConfirmId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  const RE_AUTH_DURATION = 5 * 60 * 1000;

  useEffect(() => {
    if (reAuthExpiry === null) return;
    const remaining = reAuthExpiry - Date.now();
    if (remaining <= 0) {
      setReAuthExpiry(null);
      return;
    }
    const timer = setTimeout(() => setReAuthExpiry(null), remaining);
    return () => clearTimeout(timer);
  }, [reAuthExpiry]);

  const isReAuthenticated = reAuthExpiry !== null && Date.now() < reAuthExpiry;

  const handleReAuthSuccess = () => {
    setReAuthExpiry(Date.now() + RE_AUTH_DURATION);
  };

  const initialized = useRef(false);

  // ── Initial load: page 1 of all tabs + today activity count ──
  useEffect(() => {
    if (!isAdmin) return;

    const fetchInitial = async () => {
      setLoading(true);

      const today = new Date().toISOString().split('T')[0];

      // 초기 로드는 항상 필터 없는 page 1만 로드한다. 필터 변경은 아래 page-change effect가 담당한다.
      const mQuery = supabase.from('profiles').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(0, MEMBERS_PAGE_SIZE - 1);
      const aQuery = supabase.from('user_activity_logs').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(0, ACTIVITY_PAGE_SIZE - 1);
      const nQuery = supabase.from('saved_numbers').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(0, NUMBERS_PAGE_SIZE - 1);

      const [mRes, aRes, agRes, nRes, tRes] = await Promise.all([
        mQuery,
        aQuery,
        supabase.from('user_agreements').select('*', { count: 'exact' }).order('agreed_at', { ascending: false }).range(0, AGREEMENTS_PAGE_SIZE - 1),
        nQuery,
        supabase.from('user_activity_logs').select('*', { count: 'exact', head: true }).gte('created_at', `${today}T00:00:00`).lte('created_at', `${today}T23:59:59`),
      ]);

      if (mRes.data) setProfiles(mRes.data as Profile[]);
      if (mRes.count !== null) setMembersTotal(mRes.count);
      if (aRes.data) setActivityLogs(aRes.data as ActivityLog[]);
      if (aRes.count !== null) setActivityTotal(aRes.count);
      if (agRes.data) setAgreements(agRes.data as AgreementRecord[]);
      if (agRes.count !== null) setAgreementsTotal(agRes.count);
      if (nRes.data) setAllNumbers(nRes.data as SavedNumber[]);
      if (nRes.count !== null) setNumbersTotal(nRes.count);
      if (tRes.count !== null) setTodayActivityCount(tRes.count);

      setLoading(false);
      initialized.current = true;
    };

    fetchInitial();
  }, [isAdmin]);

  // ── Page change effects (skip during initial load) ──

  useEffect(() => {
    if (!isAdmin || !initialized.current) return;
    const from = (membersPage - 1) * MEMBERS_PAGE_SIZE;
    const to = from + MEMBERS_PAGE_SIZE - 1;
    let query = supabase.from('profiles').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(from, to);
    if (debouncedMembersSearch.trim()) query = query.ilike('nickname', `%${debouncedMembersSearch.trim()}%`);
    query.then(({ data, count }) => {
      if (data) setProfiles(data as Profile[]);
      if (count !== null) setMembersTotal(count);
    });
  }, [membersPage, debouncedMembersSearch, isAdmin]);

  useEffect(() => {
    if (!isAdmin || !initialized.current) return;
    const from = (activityPage - 1) * ACTIVITY_PAGE_SIZE;
    const to = from + ACTIVITY_PAGE_SIZE - 1;
    let query = supabase.from('user_activity_logs').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(from, to);
    if (activityActionFilter) query = query.eq('action', activityActionFilter);
    query.then(({ data, count }) => {
      if (data) setActivityLogs(data as ActivityLog[]);
      if (count !== null) setActivityTotal(count);
    });
  }, [activityPage, activityActionFilter, isAdmin]);

  useEffect(() => {
    if (!isAdmin || !initialized.current) return;
    const from = (agreementsPage - 1) * AGREEMENTS_PAGE_SIZE;
    const to = from + AGREEMENTS_PAGE_SIZE - 1;
    supabase.from('user_agreements').select('*', { count: 'exact' }).order('agreed_at', { ascending: false }).range(from, to)
      .then(({ data, count }) => {
        if (data) setAgreements(data as AgreementRecord[]);
        if (count !== null) setAgreementsTotal(count);
      });
  }, [agreementsPage, isAdmin]);

  useEffect(() => {
    if (!isAdmin || !initialized.current) return;
    const from = (numbersPage - 1) * NUMBERS_PAGE_SIZE;
    const to = from + NUMBERS_PAGE_SIZE - 1;
    let query = supabase.from('saved_numbers').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(from, to);
    if (numbersGeneratorFilter) query = query.eq('generator_type', numbersGeneratorFilter);
    if (numberFilter.trim()) {
      const nums = numberFilter.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 1 && n <= 45);
      if (nums.length > 0) {
        const conditions = nums.map(n => `numbers.cs.{${n}}`).join(',');
        query = query.or(conditions);
      }
    }
    query.then(({ data, count }) => {
      if (data) setAllNumbers(data as SavedNumber[]);
      if (count !== null) setNumbersTotal(count);
    });
  }, [numbersPage, numbersGeneratorFilter, numberFilter, isAdmin]);

  const getNickname = (userId: string) => {
    const p = profiles.find((pr) => pr.id === userId);
    return p?.nickname || userId.slice(0, 8);
  };

  const getEmail = (userId: string) => {
    const p = profiles.find((pr) => pr.id === userId);
    return p?.nickname || userId.slice(0, 12) + '...';
  };

  // ── 관리자 mutation 공통 handler (서버 RPC 성공 후에만 로컬 상태 변경) ──

  const roleChangeErrorMessage = (message: string): string => {
    if (message.includes('cannot demote self')) return '자신의 관리자 권한은 해제할 수 없습니다.';
    if (message.includes('cannot remove last admin') || message.includes('last admin')) return '마지막 남은 관리자는 강등할 수 없습니다.';
    if (message.includes('target not found') || message.includes('not found')) return '대상 사용자를 찾을 수 없습니다.';
    if (message.includes('forbidden')) return '권한이 없습니다.';
    if (message.includes('invalid role')) return '잘못된 역할입니다.';
    if (message.includes('not authenticated')) return '로그인이 필요합니다.';
    return '역할 변경에 실패했습니다. 다시 시도해주세요.';
  };

  const handleChangeRole = async (targetId: string, currentRole: 'user' | 'admin') => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    setChangingRoleId(targetId);
    setActionError('');
    try {
      const { data, error } = await supabase.rpc('change_profile_role', {
        target_user_id: targetId,
        new_role: newRole,
      });
      if (error || !data) {
        if (import.meta.env.DEV) console.error('[Admin] 역할 변경 실패:', error?.message ?? 'no row');
        setActionError(roleChangeErrorMessage(error?.message ?? ''));
        return;
      }
      // 서버 성공 후에만 로컬 역할 변경
      setProfiles((prev) => prev.map((pr) => (pr.id === targetId ? { ...pr, role: newRole } : pr)));
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Admin] 역할 변경 예외:', err);
      setActionError('역할 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setChangingRoleId(null);
    }
  };

  const deleteNumberErrorMessage = (message: string): string => {
    if (message.includes('not found')) return '저장 번호를 찾을 수 없습니다.';
    if (message.includes('forbidden')) return '권한이 없습니다.';
    return '삭제에 실패했습니다. 다시 시도해주세요.';
  };

  const handleDeleteNumber = async (id: string) => {
    setDeletingNumberId(id);
    setActionError('');
    try {
      const { data, error } = await supabase.rpc('admin_delete_saved_number', { p_number_id: id });
      if (error || !data) {
        if (import.meta.env.DEV) console.error('[Admin] 번호 삭제 실패:', error?.message ?? 'no row');
        setActionError(deleteNumberErrorMessage(error?.message ?? ''));
        return;
      }
      // 서버 성공 후에만 목록에서 제거
      setAllNumbers((prev) => prev.filter((x) => x.id !== id));
      setDeleteNumberConfirmId(null);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Admin] 번호 삭제 예외:', err);
      setActionError('삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeletingNumberId(null);
    }
  };

  const ReAuthLockButton = ({
    isReAuthenticated,
    reAuthExpiry,
    onRequestReAuth,
  }: {
    isReAuthenticated: boolean;
    reAuthExpiry: number | null;
    onRequestReAuth: () => void;
  }) => {
    if (isReAuthenticated) {
      const remaining = reAuthExpiry ? Math.max(0, Math.ceil((reAuthExpiry - Date.now()) / 60000)) : 0;
      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
            <i className="ri-shield-check-line text-xs"></i>
            인증됨 · {remaining}분
          </span>
        </div>
      );
    }
    return (
      <button
        onClick={onRequestReAuth}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-300 text-xs text-foreground-500 hover:bg-background-100 hover:border-background-400 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-lock-line text-xs"></i>
        민감 정보 조회
      </button>
    );
  };

  // Mobile card views for tables
  const renderMembersMobile = () => (
    <div className="md:hidden space-y-3">
      {profiles.map((p) => (
        <div key={p.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs text-foreground-500">
              {isReAuthenticated ? p.id.slice(0, 16) + '...' : `••••-••••-${p.id.slice(-4)}`}
            </span>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              p.role === 'admin' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'
            }`}>
              {p.role === 'admin' ? '관리자' : '일반'}
            </span>
          </div>
          <p className="text-sm text-foreground-800 font-medium mb-1">{p.nickname || '-'}</p>
          <p className="text-xs text-foreground-400 mb-3">
            가입: {new Date(p.created_at).toLocaleDateString('ko-KR')}
          </p>
          <button
            onClick={() => handleChangeRole(p.id, p.role)}
            disabled={changingRoleId === p.id || (p.role === 'admin' && p.id === user?.id)}
            className="text-xs px-3 py-1.5 rounded-lg border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {changingRoleId === p.id ? '처리 중...' : p.role === 'admin' ? '관리자 해제' : '관리자 지정'}
          </button>
        </div>
      ))}
    </div>
  );

  const renderActivityMobile = () => (
    <div className="md:hidden space-y-3">
      {activityLogs.map((log) => (
        <div key={log.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">
              {isReAuthenticated ? getEmail(log.user_id) : '••••'}
            </span>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              log.action === 'login' || log.action === 'register'
                ? 'bg-secondary-100 text-secondary-700'
                : log.action === 'logout'
                ? 'bg-background-200 text-foreground-500'
                : 'bg-primary-100 text-primary-700'
            }`}>
              {actionLabels[log.action] || log.action}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-1">
            {log.detail ? (
              isReAuthenticated ? (
                <span className="font-mono break-all">{JSON.stringify(log.detail).slice(0, 80)}...</span>
              ) : (
                <span className="text-foreground-300 flex items-center gap-1">
                  <i className="ri-lock-line text-[10px]"></i>마스킹됨
                </span>
              )
            ) : '-'}
          </p>
          <p className="text-xs text-foreground-400">
            {new Date(log.created_at).toLocaleString('ko-KR')}
          </p>
        </div>
      ))}
    </div>
  );

  const renderAgreementsMobile = () => (
    <div className="md:hidden space-y-3">
      {agreements.map((ag) => (
        <div key={ag.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-600">{getEmail(ag.user_id)}</span>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              ag.agreed ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'
            }`}>
              {ag.agreed ? '동의' : '거부'}
            </span>
          </div>
          <p className="text-sm text-foreground-700 mb-1">
            {ag.terms_id === 1 ? '이용약관' : ag.terms_id === 2 ? '개인정보처리방침' : `약관 #${ag.terms_id}`}
          </p>
          <p className="text-xs text-foreground-400">
            {new Date(ag.agreed_at).toLocaleString('ko-KR')}
          </p>
        </div>
      ))}
    </div>
  );

  const renderNumbersMobile = () => (
    <div className="md:hidden space-y-3">
      {allNumbers.map((n) => (
        <div key={n.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">{getEmail(n.user_id)}</span>
            <span className="text-xs text-foreground-300 bg-background-100 px-2 py-0.5 rounded-full">
              {n.generator_type === 'random' ? '랜덤' : n.generator_type === 'lucky' ? '행운' : n.generator_type === 'smart' ? '스마트' : '수동'}
            </span>
          </div>
          <div className="flex gap-1 mb-2">
            {n.numbers.map((num) => (
              <span key={num} className="inline-flex w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-xs items-center justify-center font-bold">
                {num}
              </span>
            ))}
            {n.bonus && (
              <span className="inline-flex w-7 h-7 rounded-full bg-accent-100 text-accent-700 text-xs items-center justify-center font-bold">
                {n.bonus}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-foreground-400">{n.label || '라벨 없음'}</span>
            <span className="text-xs text-foreground-400">{new Date(n.created_at).toLocaleDateString('ko-KR')}</span>
          </div>
          {deleteNumberConfirmId === n.id ? (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-xs text-foreground-400">삭제?</span>
              <button
                onClick={() => handleDeleteNumber(n.id)}
                disabled={deletingNumberId === n.id}
                className="px-3 py-1.5 rounded-lg text-xs bg-accent-500 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deletingNumberId === n.id ? '삭제 중...' : '확인'}
              </button>
              <button
                onClick={() => setDeleteNumberConfirmId(null)}
                disabled={deletingNumberId === n.id}
                className="px-3 py-1.5 rounded-lg text-xs border border-background-300 text-foreground-500 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                취소
              </button>
            </div>
          ) : (
            <button
              onClick={() => setDeleteNumberConfirmId(n.id)}
              className="mt-2 text-xs px-3 py-1.5 rounded-lg text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              삭제
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'members':
        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-foreground-800">회원 목록</h3>
              <ReAuthLockButton
                isReAuthenticated={isReAuthenticated}
                reAuthExpiry={reAuthExpiry}
                onRequestReAuth={() => setReAuthModalOpen(true)}
              />
            </div>
            {actionError && (
              <div className="mb-4 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                <p className="text-accent-700 text-sm">{actionError}</p>
              </div>
            )}
            {/* Search filter */}
            <div className="mb-4">
              <div className="relative max-w-xs">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={membersSearch}
                  onChange={(e) => { setMembersSearch(e.target.value); setMembersPage(1); }}
                  placeholder="닉네임 검색..."
                  className="w-full pl-9 pr-8 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                {membersSearch && (
                  <button
                    onClick={() => { setMembersSearch(''); setMembersPage(1); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-300 hover:text-foreground-500 cursor-pointer"
                  >
                    <i className="ri-close-circle-line text-sm"></i>
                  </button>
                )}
              </div>
              {membersSearch && (
                <p className="text-xs text-foreground-400 mt-1.5">
                  &quot;{membersSearch}&quot; 검색 결과: <strong className="text-foreground-800">{membersTotal.toLocaleString()}명</strong>
                </p>
              )}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">사용자 ID</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">닉네임</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">역할</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">가입일</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr key={p.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="py-3 px-4 text-foreground-600 font-mono text-xs">
                        {isReAuthenticated ? (
                          <span className="text-foreground-700">{p.id}</span>
                        ) : (
                          <span className="text-foreground-300 select-none">
                            ••••-••••-••••-{p.id.slice(-4)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-foreground-800">{p.nickname || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.role === 'admin' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'
                        }`}>
                          {p.role === 'admin' ? '관리자' : '일반'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-foreground-500 text-xs">
                        {new Date(p.created_at).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleChangeRole(p.id, p.role)}
                          disabled={changingRoleId === p.id || (p.role === 'admin' && p.id === user?.id)}
                          className="text-xs px-3 py-1 rounded-lg border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {changingRoleId === p.id ? '처리 중...' : p.role === 'admin' ? '관리자 해제' : '관리자 지정'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            {renderMembersMobile()}
            <Pagination
              currentPage={membersPage}
              totalCount={membersTotal}
              pageSize={MEMBERS_PAGE_SIZE}
              onPageChange={setMembersPage}
            />
          </div>
        );

      case 'activity':
        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-foreground-800">활동 이력</h3>
              <ReAuthLockButton
                isReAuthenticated={isReAuthenticated}
                reAuthExpiry={reAuthExpiry}
                onRequestReAuth={() => setReAuthModalOpen(true)}
              />
            </div>
            {/* Action filter */}
            <div className="mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-400 flex-shrink-0">
                  <i className="ri-filter-3-line mr-1"></i>활동 유형
                </span>
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => { setActivityActionFilter(''); setActivityPage(1); }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                      !activityActionFilter
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'bg-background-50 border-background-300 text-foreground-400 hover:border-background-400'
                    }`}
                  >
                    전체
                  </button>
                  {Object.entries(actionLabels).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => { setActivityActionFilter(key); setActivityPage(1); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                        activityActionFilter === key
                          ? 'bg-primary-100 border-primary-300 text-primary-700'
                          : 'bg-background-50 border-background-300 text-foreground-400 hover:border-background-400'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">사용자</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">활동</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">상세</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">일시</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLogs.map((log) => (
                    <tr key={log.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="py-2.5 px-4 text-foreground-600 text-xs">
                        {isReAuthenticated ? (
                          getEmail(log.user_id)
                        ) : (
                          <span className="text-foreground-300 select-none">••••</span>
                        )}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.action === 'login' || log.action === 'register'
                            ? 'bg-secondary-100 text-secondary-700'
                            : log.action === 'logout'
                            ? 'bg-background-200 text-foreground-500'
                            : 'bg-primary-100 text-primary-700'
                        }`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-foreground-500 text-xs">
                        {log.detail ? (
                          isReAuthenticated ? (
                            <span className="font-mono break-all">
                              {JSON.stringify(log.detail)}
                            </span>
                          ) : (
                            <span className="text-foreground-300 select-none flex items-center gap-1">
                              <i className="ri-lock-line text-[10px]"></i>
                              마스킹됨
                            </span>
                          )
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-foreground-400 text-xs whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('ko-KR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderActivityMobile()}
            <Pagination
              currentPage={activityPage}
              totalCount={activityTotal}
              pageSize={ACTIVITY_PAGE_SIZE}
              onPageChange={setActivityPage}
            />
          </div>
        );

      case 'agreements':
        return (
          <div>
            <h3 className="font-heading text-lg text-foreground-800 mb-4">약관 동의 이력</h3>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">사용자</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">약관 ID</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">동의 여부</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">동의 일시</th>
                  </tr>
                </thead>
                <tbody>
                  {agreements.map((ag) => (
                    <tr key={ag.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="py-2.5 px-4 text-foreground-600 text-xs">{getEmail(ag.user_id)}</td>
                      <td className="py-2.5 px-4 text-foreground-600">
                        {ag.terms_id === 1 ? '이용약관' : ag.terms_id === 2 ? '개인정보처리방침' : `약관 #${ag.terms_id}`}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          ag.agreed ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'
                        }`}>
                          {ag.agreed ? '동의' : '거부'}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-foreground-400 text-xs whitespace-nowrap">
                        {new Date(ag.agreed_at).toLocaleString('ko-KR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderAgreementsMobile()}
            <Pagination
              currentPage={agreementsPage}
              totalCount={agreementsTotal}
              pageSize={AGREEMENTS_PAGE_SIZE}
              onPageChange={setAgreementsPage}
            />
          </div>
        );

      case 'numbers':
        return (
          <div>
            <h3 className="font-heading text-lg text-foreground-800 mb-4">저장된 번호</h3>
            {actionError && (
              <div className="mb-4 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                <p className="text-accent-700 text-sm">{actionError}</p>
              </div>
            )}
            {/* Filters: generator type + number inclusion */}
            <div className="mb-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-400 flex-shrink-0">
                  <i className="ri-filter-3-line mr-1"></i>생성 방식
                </span>
                <div className="flex flex-wrap gap-1">
                  {[
                    { value: '', label: '전체' },
                    { value: 'random', label: '랜덤' },
                    { value: 'lucky', label: '행운' },
                    { value: 'smart', label: '스마트' },
                    { value: 'manual', label: '수동' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setNumbersGeneratorFilter(opt.value); setNumbersPage(1); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                        numbersGeneratorFilter === opt.value
                          ? 'bg-primary-100 border-primary-300 text-primary-700'
                          : 'bg-background-50 border-background-300 text-foreground-400 hover:border-background-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400 flex-shrink-0">
                  <i className="ri-search-line mr-1"></i>번호 포함
                </span>
                <div className="relative flex-1 max-w-xs">
                  <input
                    type="text"
                    value={numberFilter}
                    onChange={(e) => { setNumberFilter(e.target.value); setNumbersPage(1); }}
                    placeholder="예: 7, 17, 33"
                    className="w-full px-3 py-1.5 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  {numberFilter && (
                    <button
                      onClick={() => { setNumberFilter(''); setNumbersPage(1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-300 hover:text-foreground-500 cursor-pointer"
                    >
                      <i className="ri-close-circle-line text-sm"></i>
                    </button>
                  )}
                </div>
                {numberFilter.trim() && (
                  <span className="text-xs text-foreground-400">
                    결과: <strong className="text-foreground-800">{numbersTotal.toLocaleString()}건</strong>
                  </span>
                )}
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">사용자</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">번호</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">보너스</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">방식</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">라벨</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">생성일</th>
                    <th className="text-left py-3 px-4 text-foreground-400 font-medium">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {allNumbers.map((n) => (
                    <tr key={n.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="py-2.5 px-4 text-foreground-600 text-xs">{getEmail(n.user_id)}</td>
                      <td className="py-2.5 px-4">
                        <div className="flex gap-1">
                          {n.numbers.map((num) => (
                            <span key={num} className="inline-flex w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs items-center justify-center font-bold">
                              {num}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 px-4">
                        {n.bonus && (
                          <span className="inline-flex w-6 h-6 rounded-full bg-accent-100 text-accent-700 text-xs items-center justify-center font-bold">
                            {n.bonus}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-foreground-500 text-xs">
                        {n.generator_type === 'random' ? '랜덤' : n.generator_type === 'lucky' ? '행운' : n.generator_type === 'smart' ? '스마트' : '수동'}
                      </td>
                      <td className="py-2.5 px-4 text-foreground-500 text-xs">{n.label || '-'}</td>
                      <td className="py-2.5 px-4 text-foreground-400 text-xs whitespace-nowrap">
                        {new Date(n.created_at).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="py-2.5 px-4">
                        {deleteNumberConfirmId === n.id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-foreground-400">삭제?</span>
                            <button
                              onClick={() => handleDeleteNumber(n.id)}
                              disabled={deletingNumberId === n.id}
                              className="text-xs px-2 py-1 rounded-lg bg-accent-500 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              {deletingNumberId === n.id ? '삭제 중...' : '확인'}
                            </button>
                            <button
                              onClick={() => setDeleteNumberConfirmId(null)}
                              disabled={deletingNumberId === n.id}
                              className="text-xs px-2 py-1 rounded-lg border border-background-300 text-foreground-500 cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteNumberConfirmId(n.id)}
                            className="text-xs px-2 py-1 rounded-lg text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            삭제
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderNumbersMobile()}
            <Pagination
              currentPage={numbersPage}
              totalCount={numbersTotal}
              pageSize={NUMBERS_PAGE_SIZE}
              onPageChange={setNumbersPage}
            />
          </div>
        );

      case 'stats':
        return <StatsCharts />;

      case 'draws':
        return <DrawManager />;

      case 'errors':
        return <ErrorLogManager />;

      case 'terms':
        return <TermsManager />;

      default:
        return null;
    }
  };

  const tabs: { key: AdminTab; label: string; icon: string }[] = [
    { key: 'members', label: '회원 관리', icon: 'ri-user-settings-line' },
    { key: 'activity', label: '활동 이력', icon: 'ri-history-line' },
    { key: 'agreements', label: '약관 동의', icon: 'ri-file-text-line' },
    { key: 'numbers', label: '저장 번호', icon: 'ri-numbers-line' },
    { key: 'terms', label: '약관 관리', icon: 'ri-file-text-line' },
    { key: 'stats', label: '통계', icon: 'ri-bar-chart-line' },
    { key: 'draws', label: '당첨번호', icon: 'ri-trophy-line' },
    { key: 'errors', label: '오류 로그', icon: 'ri-error-warning-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-16 md:pt-20 pb-12">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-heading text-xl md:text-2xl text-foreground-800">관리자 대시보드</h1>
              <p className="text-foreground-400 text-xs md:text-sm mt-1">회원, 활동, 약관 동의 이력을 관리합니다.</p>
            </div>
            <div className="flex flex-wrap gap-2 md:gap-3">
              <div className="px-3 py-1.5 md:px-4 md:py-2 rounded-xl bg-background-100 border border-background-200 text-xs md:text-sm text-foreground-500">
                총 회원: <strong className="text-foreground-800">{membersTotal.toLocaleString()}명</strong>
              </div>
              <div className="px-3 py-1.5 md:px-4 md:py-2 rounded-xl bg-background-100 border border-background-200 text-xs md:text-sm text-foreground-500">
                오늘 활동: <strong className="text-foreground-800">{todayActivityCount.toLocaleString()}건</strong>
              </div>
            </div>
          </div>

          {/* Tabs — horizontal scroll on mobile */}
          <div className="relative mb-6">
            <div className="flex gap-1 border-b border-background-200 overflow-x-auto scrollbar-hide pb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setActionError(''); setDeleteNumberConfirmId(null); }}
                  className={`flex items-center gap-1.5 px-3 md:px-4 py-2.5 text-xs md:text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className={`${tab.icon} text-sm md:text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 mx-auto mb-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-foreground-400 text-sm">데이터를 불러오는 중...</p>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
      <div className="md:hidden h-16"></div>
      <MobileBottomNav />

      <ReAuthModal
        open={reAuthModalOpen}
        onClose={() => setReAuthModalOpen(false)}
        onSuccess={handleReAuthSuccess}
      />
    </div>
  );
}